import type { ChatConfig } from "../types/config.js";
import type { RouteInfo, RouteManifest } from "../types/routes.js";
import type { ToolCallResult, ToolDefinition, ToolManifest } from "../types/tools.js";
import type { RouteSource, RouteSourceInput, ToolSource, ToolSourceInput } from "./sources.js";
import { normalizeRouteSources, normalizeToolSources } from "./sources.js";

export type YakHandlerConfig = {
  routes: RouteSourceInput;
  tools?: ToolSourceInput;
};

export function createYakHandler(config: YakHandlerConfig) {
  const routeSources = normalizeRouteSources(config.routes);
  const toolSources = normalizeToolSources(config.tools);

  const GET = async function handleConfig(_req: Request): Promise<Response> {
    const chatConfig: ChatConfig = {
      routes: await buildRouteManifest(routeSources),
    };

    if (toolSources.length > 0) {
      const { manifest } = await buildToolRegistry(toolSources);
      chatConfig.tools = manifest;
    }

    return jsonResponse(chatConfig, 200, {
      "Cache-Control": "no-store",
    });
  };

  const POST = async function handleToolCall(req: Request): Promise<Response> {
    if (toolSources.length === 0) {
      return errorResponse("Tool execution is not configured", 501);
    }

    let payload: unknown;

    try {
      payload = await req.json();
    } catch {
      return errorResponse("Invalid JSON payload", 400);
    }

    if (!isHostToolCallPayload(payload)) {
      return errorResponse("Invalid tool call payload", 400);
    }

    try {
      const { lookup } = await buildToolRegistry(toolSources);
      const owner = lookup.get(payload.name);

      if (!owner) {
        return createToolErrorResponse(`Tool '${payload.name}' is not registered`);
      }

      if (!owner.executeTool) {
        return createToolErrorResponse(`Tool '${payload.name}' does not expose an executor`);
      }

      const result = await owner.executeTool(payload.name, payload.args, req);

      const successResult: ToolCallResult = {
        ok: true,
        result,
      };

      return jsonResponse(successResult);
    } catch (error) {
      return createToolErrorResponse(extractErrorMessage(error));
    }
  };

  return { GET, POST };
}

export type YakConfigHandlerConfig = {
  routes: RouteSourceInput;
  tools?: ToolSourceInput;
};

export function createYakConfigHandler(config: YakConfigHandlerConfig) {
  const routeSources = normalizeRouteSources(config.routes);
  const toolSources = normalizeToolSources(config.tools);

  return async function handleConfig(_req: Request): Promise<Response> {
    const chatConfig: ChatConfig = {
      routes: await buildRouteManifest(routeSources),
    };

    if (toolSources.length > 0) {
      const { manifest } = await buildToolRegistry(toolSources);
      chatConfig.tools = manifest;
    }

    return jsonResponse(chatConfig, 200, {
      "Cache-Control": "no-store",
    });
  };
}

export type YakToolsHandlerConfig = {
  tools: ToolSourceInput;
};

export function createYakToolsHandler(config: YakToolsHandlerConfig) {
  const toolSources = normalizeToolSources(config.tools);

  if (toolSources.length === 0) {
    throw new Error("createYakToolsHandler requires at least one tool source");
  }

  return async function handleTools(req: Request): Promise<Response> {
    let payload: unknown;

    try {
      payload = await req.json();
    } catch {
      return errorResponse("Invalid JSON payload", 400);
    }

    if (!isHostToolCallPayload(payload)) {
      return errorResponse("Invalid tool call payload", 400);
    }

    try {
      const { lookup } = await buildToolRegistry(toolSources);
      const owner = lookup.get(payload.name);

      if (!owner) {
        return createToolErrorResponse(`Tool '${payload.name}' is not registered`);
      }

      if (!owner.executeTool) {
        return createToolErrorResponse(`Tool '${payload.name}' does not expose an executor`);
      }

      const result = await owner.executeTool(payload.name, payload.args, req);
      const successResult: ToolCallResult = {
        ok: true,
        result,
      };

      return jsonResponse(successResult);
    } catch (error) {
      return createToolErrorResponse(extractErrorMessage(error));
    }
  };
}

async function buildRouteManifest(routeSources: RouteSource[]): Promise<RouteManifest> {
  const entries = await Promise.all(
    routeSources.map(async (source) => ({
      id: source.id ?? "routes",
      routes: await source.getRoutes(),
    }))
  );

  const merged: RouteInfo[] = [];
  const seen = new Set<string>();

  for (const entry of entries) {
    for (const route of entry.routes) {
      const key = route.path;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(route);
      }
    }
  }

  merged.sort((a, b) => a.path.localeCompare(b.path));

  return {
    routes: merged,
    generated_at: new Date().toISOString(),
    sources: entries.map((entry) => ({ id: entry.id, count: entry.routes.length })),
  };
}

type ToolRegistry = {
  manifest: ToolManifest;
  lookup: Map<string, ToolSource>;
};

async function buildToolRegistry(toolSources: ToolSource[]): Promise<ToolRegistry> {
  const entries = await Promise.all(
    toolSources.map(async (source) => ({
      id: source.id ?? "tools",
      source,
      tools: await source.getTools(),
    }))
  );

  const manifestTools: ToolDefinition[] = [];
  const lookup = new Map<string, ToolSource>();

  for (const entry of entries) {
    for (const tool of entry.tools) {
      manifestTools.push(tool);
      lookup.set(tool.name, entry.source);
    }
  }

  return {
    manifest: {
      tools: manifestTools,
      generated_at: new Date().toISOString(),
      sources: entries.map((entry) => ({ id: entry.id, count: entry.tools.length })),
    },
    lookup,
  };
}

/**
 * Payload received from the host (id is optional since YakClient handles correlation)
 */
type HostToolCallPayload = {
  name: string;
  args: unknown;
};

function isHostToolCallPayload(payload: unknown): payload is HostToolCallPayload {
  return (
    typeof payload === "object" &&
    payload !== null &&
    typeof (payload as HostToolCallPayload).name === "string" &&
    "args" in payload
  );
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "An unknown error occurred";
}

function jsonResponse(body: unknown, status = 200, headers: Record<string, string> = {}): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });
}

function errorResponse(message: string, status: number): Response {
  return jsonResponse({ error: message }, status);
}

function createToolErrorResponse(error: string): Response {
  const errorResult: ToolCallResult = {
    ok: false,
    error,
  };

  return jsonResponse(errorResult);
}
